import {
  actionButton,
  actionLabel,
  displayName,
  groupOf,
  type StudioAction,
  type StudioActionGroup,
} from "@/components/ai/chat/studioActionFence";
import { seedingWaitLabel, seedingWaits } from "./deployProblems";
import type { ExplorerItem, ExplorerSection } from "./explorerData";
import { scriptTemplate } from "./scriptTemplates";
import {
  canPreviewSample,
  type DropEdge,
  isSchemaEditorId,
  isSuiteRowId,
  isUntitledScriptId,
  type StagedResource,
  type StagedRole,
  type StagedSchema,
  type StageTabId,
  type StageTabRole,
  schemaEditorId,
  schemaIdOfInstance,
  schemaInstanceId,
  unattachedSchemaEditorId,
} from "./stagingModel";
import {
  droppableFor,
  type StagedSnapshot,
  type StudioState,
  schemaAttachedTo,
} from "./studioSnapshot";

// A parsed action checked against the live Studio: the same rules the
// drag-and-drop uses decide whether a stage may land, and an explorer
// action names the nodes it will open. The result is data — the page's
// hook turns an effect into provider calls — so the rules test without
// React.

export type StudioEffect =
  | { kind: "explorer.expand"; items: string[]; sections: string[] }
  | { kind: "explorer.collapse"; items: string[]; sections: string[] }
  | { kind: "explorer.filter"; text: string }
  | {
      kind: "stage.resource";
      role: StagedRole;
      resource: StagedResource;
      tab: StageTabId;
    }
  | { kind: "stage.schema"; resource: StagedResource; tab: StageTabRole }
  | { kind: "stage.suite"; rowId: string; tab: "tests" }
  | { kind: "tab.set"; tab: StageTabId }
  | { kind: "view.set"; view: "stages" | "full" }
  | { kind: "select"; id: string; tab: StageTabId; test: boolean }
  | { kind: "output.set"; collapsed: boolean }
  | { kind: "run.pipeline" }
  | { kind: "run.script"; resource: StagedResource; schema?: StagedSchema }
  | { kind: "run.tests" }
  | { kind: "unstage"; id: string; tab: StageTabId; untitled: boolean }
  | { kind: "reorder"; id: string; targetId: string; edge: DropEdge }
  | {
      kind: "schema.attach";
      resource: StagedResource;
      target: { role: StagedRole; id: string };
      tab: StageTabId;
      /** The sink's current schema this one replaces, by name. */
      replaces: string | null;
    }
  | {
      kind: "schema.detach";
      schemaId: string;
      target: { role: StagedRole; id: string };
      tab: StageTabId;
    }
  | { kind: "input.set"; source: "document" | "batch" | "text"; text?: string }
  | { kind: "input.load"; resource: StagedResource }
  | { kind: "script.template"; script: string; name: string; tab: "transform" }
  | { kind: "pipeline.template" };

interface ResolvedBase {
  group: StudioActionGroup;
  label: string;
  /** The click row's button text. */
  button: string;
}

export type ResolvedAction =
  | (ResolvedBase & { ok: true; effect: StudioEffect })
  | (ResolvedBase & { ok: false; reason: string });

export interface ExplorerHit {
  sectionId: string;
  path: ExplorerItem[];
}

/** The node an explorer id names, or undefined. */
function nodeOf(
  sections: ExplorerSection[],
  id: string,
): ExplorerItem | undefined {
  const path = findExplorerNode(sections, id)?.path;
  return path && path.length > 0 ? path[path.length - 1] : undefined;
}

type ExplorerAction = Extract<
  StudioAction,
  { action: "explorer.expand" | "explorer.collapse" }
>;
type StageAction = Extract<StudioAction, { action: "stage" }>;

function findIn(
  items: ExplorerItem[],
  id: string,
  trail: ExplorerItem[],
): ExplorerItem[] | null {
  for (const item of items) {
    const path = [...trail, item];
    const hit = item.id === id ? path : findIn(item.children ?? [], id, path);
    if (hit) return hit;
  }
  return null;
}

/** The section and the path (root … node) of an explorer id; null when
 *  the tree has no such node. A section id resolves with an empty path. */
export function findExplorerNode(
  sections: ExplorerSection[],
  id: string,
): ExplorerHit | null {
  for (const section of sections) {
    if (section.id === id) return { sectionId: section.id, path: [] };
    const path = findIn(section.items, id, []);
    if (path) return { sectionId: section.id, path };
  }
  return null;
}

function isExpandable(item: ExplorerItem): boolean {
  return (item.children?.length ?? 0) > 0 || !!item.childrenLoading;
}

// What a resolver decides before the label and group are attached.
type Outcome =
  | { ok: true; effect: StudioEffect }
  | { ok: false; reason: string };

function refuse(reason: string): Outcome {
  return { ok: false, reason };
}

function accept(effect: StudioEffect): Outcome {
  return { ok: true, effect };
}

// expand: a node plus every ancestor, so the row ends up on screen;
// collapse: just the named nodes.
function explorerTargets(action: ExplorerAction, state: StudioState) {
  const withAncestors = action.action === "explorer.expand";
  const items = new Set<string>();
  const sections = new Set<string>();
  const unknown: string[] = [];
  for (const id of action.ids) {
    const hit = findExplorerNode(state.sections, id);
    if (!hit) {
      unknown.push(id);
      continue;
    }
    if (withAncestors || hit.path.length === 0) sections.add(hit.sectionId);
    const nodes = withAncestors ? hit.path : hit.path.slice(-1);
    for (const node of nodes) if (isExpandable(node)) items.add(node.id);
  }
  return { items: [...items], sections: [...sections], unknown };
}

function resolveExplorer(action: ExplorerAction, state: StudioState): Outcome {
  const { items, sections, unknown } = explorerTargets(action, state);
  if (items.length === 0 && sections.length === 0) {
    return refuse(
      `no such item in the explorer: ${unknown.map(displayName).join(", ")}`,
    );
  }
  return accept({ kind: action.action, items, sections });
}

const STAGED_ROLES: ReadonlySet<string> = new Set([
  "source",
  "transform",
  "sink",
]);

function stagedRoleOf(action: StageAction): StagedRole | null {
  const role = action.role;
  return role !== undefined && STAGED_ROLES.has(role)
    ? (role as StagedRole)
    : null;
}

const TAB_OF_ROLE: Record<StagedRole, StageTabId> = {
  source: "source",
  transform: "transform",
  sink: "sink",
};

function schemaTab(tab: StageTabId): StageTabRole {
  return tab === "tests" ? "sink" : tab;
}

function toResource(item: ExplorerItem): StagedResource {
  return {
    id: item.id,
    name: item.name,
    icon: item.icon,
    ...(item.provider ? { provider: item.provider } : {}),
  };
}

function resolveSuiteStage(
  action: StageAction,
  item: ExplorerItem,
  state: StudioState,
): Outcome {
  if (action.role && action.role !== "tests") {
    return refuse(`${item.name} can only go to Tests`);
  }
  const drop = droppableFor(item, state);
  if (!drop.roles.includes("tests")) {
    const why = drop.reason ?? "already staged";
    return refuse(`${item.name} can't be staged (${why})`);
  }
  return accept({ kind: "stage.suite", rowId: item.id, tab: "tests" });
}

function resolveSchemaStage(
  action: StageAction,
  item: ExplorerItem,
  state: StudioState,
): Outcome {
  if (action.role && action.role !== "schema") {
    const why = "it attaches to a stage, not a role";
    return refuse(`${item.name} is a schema — ${why}`);
  }
  const drop = droppableFor(item, state);
  if (!drop.roles.includes("schema")) {
    const why = drop.reason ?? "no slot";
    return refuse(`${item.name} can't be staged (${why})`);
  }
  return accept({
    kind: "stage.schema",
    resource: toResource(item),
    tab: schemaTab(state.tab),
  });
}

function resolveResourceStage(
  action: StageAction,
  item: ExplorerItem,
  state: StudioState,
): Outcome {
  const role = stagedRoleOf(action);
  if (!role) return refuse("say which role: source, transform or sink");
  const drop = droppableFor(item, state);
  if (drop.staged) {
    return refuse(`${item.name} is already staged as ${drop.staged}`);
  }
  if (!drop.roles.includes(role)) {
    if (drop.roles.length === 0) {
      return refuse(drop.reason ?? `${item.name} can't be staged`);
    }
    const why = drop.reason ?? `only ${drop.roles.join(", ")}`;
    return refuse(`${item.name} can't be a ${role} (${why})`);
  }
  return accept({
    kind: "stage.resource",
    role,
    resource: toResource(item),
    tab: TAB_OF_ROLE[role],
  });
}

function resolveStage(action: StageAction, state: StudioState): Outcome {
  const item = nodeOf(state.sections, action.id);
  if (!item)
    return refuse(`no such item in the explorer: ${displayName(action.id)}`);
  if (isSuiteRowId(item.id)) return resolveSuiteStage(action, item, state);
  if (item.id.startsWith("schema:")) {
    return resolveSchemaStage(action, item, state);
  }
  return resolveResourceStage(action, item, state);
}

const STAGES_FIRST = "switch to Stages first";
const PIPELINE_RUN_ID = "pipeline";

// Stages → Full waits for a dropped stage's schema seed, as the toolbar's
// switch does: the Full view's code is generated from staging, and the
// landing seed regenerates it under whatever the AI (or the user) edits
// there. Full → Stages is always accepted here; the effect's hand-over
// refuses it while the code has errors.
function resolveSetView(view: "stages" | "full", state: StudioState): Outcome {
  if (view === "full") {
    const wait = seedingWaits(state.seedingStageIds, state.staged)[0];
    if (wait) return refuse(seedingWaitLabel(wait));
  }
  return accept({ kind: "view.set", view });
}

// A staged row's role and tab, or a test — what `select` needs to land.
function stagedRowOf(
  staged: StagedSnapshot,
  id: string,
): { tab: StageTabId; test: boolean; name: string } | null {
  const roles: Array<[StagedRole, StagedResource[]]> = [
    ["source", staged.sources],
    ["transform", staged.transforms],
    ["sink", staged.sinks],
  ];
  for (const [role, items] of roles) {
    const item = items.find((i) => i.id === id);
    if (item) return { tab: role, test: false, name: item.name };
  }
  const test = staged.tests.find((t) => t.id === id);
  return test ? { tab: "tests", test: true, name: test.name } : null;
}

// The schema a select names: a bare "schema:<service>:<id>" or the
// Studio's own instance form "schema:<service>:<id>|<block>"; null for
// anything else.
function schemaIdOf(id: string): string | null {
  const instance = schemaIdOfInstance(id);
  if (instance !== null) return instance;
  return id.startsWith("schema:") && !isSchemaEditorId(id) ? id : null;
}

// Where a staged schema shows: its first attachment's block, else the
// bottom block of the tab it is pending on.
function schemaSelection(
  schema: StagedSchema,
): { id: string; tab: StageTabId } | null {
  const attachment = schema.attachments[0];
  if (attachment) {
    return {
      id: schemaInstanceId(schema.id, schemaEditorId(attachment.id)),
      tab: attachment.role,
    };
  }
  const waitingOn = schema.pending?.[0];
  if (waitingOn) {
    return {
      id: schemaInstanceId(schema.id, unattachedSchemaEditorId(waitingOn)),
      tab: waitingOn,
    };
  }
  return null;
}

function resolveSelectSchema(schemaId: string, state: StudioState): Outcome {
  const name = nodeOf(state.sections, schemaId)?.name ?? displayName(schemaId);
  const staged = state.staged.schemas.find((s) => s.id === schemaId);
  const where = staged ? schemaSelection(staged) : null;
  if (!where)
    return refuse(`${name} is not staged — attach it to a stage first`);
  return accept({ kind: "select", id: where.id, tab: where.tab, test: false });
}

function resolveSelect(
  action: Extract<StudioAction, { action: "select" }>,
  state: StudioState,
): Outcome {
  if (state.view === "full") return refuse(STAGES_FIRST);
  const schemaId = schemaIdOf(action.id);
  if (schemaId !== null) return resolveSelectSchema(schemaId, state);
  const row = stagedRowOf(state.staged, action.id);
  if (!row) return refuse(`${displayName(action.id)} is not staged`);
  return accept({
    kind: "select",
    id: action.id,
    tab: row.tab,
    test: row.test,
  });
}

function resolveRunPipeline(state: StudioState): Outcome {
  if (state.editsLocked) return refuse("running pipeline is view-only");
  if (state.staged.runBlockReason) return refuse(state.staged.runBlockReason);
  if (state.staged.runningId === PIPELINE_RUN_ID)
    return refuse("already running");
  return accept({ kind: "run.pipeline" });
}

function resolveRunScript(
  action: Extract<StudioAction, { action: "run.script" }>,
  state: StudioState,
): Outcome {
  const resource = state.staged.transforms.find((t) => t.id === action.id);
  if (!resource)
    return refuse(`${displayName(action.id)} is not a staged transform`);
  if (state.staged.runningId === resource.id) return refuse("already running");
  const schema = schemaAttachedTo(
    state.staged.schemas,
    "transform",
    resource.id,
  );
  return accept({ kind: "run.script", resource, schema });
}

function resolveRunTests(state: StudioState): Outcome {
  if (state.staged.suiteName === null) return refuse("no test suite staged");
  if (state.staged.tests.length === 0) return refuse("the suite has no tests");
  if (state.staged.runningId === "suite-tests")
    return refuse("already running");
  return accept({ kind: "run.tests" });
}

const VIEW_ONLY = "running pipeline is view-only";

// A staged stage row (source / transform / sink) with its role.
function stageOf(
  staged: StagedSnapshot,
  id: string,
): { role: StagedRole; item: StagedResource } | null {
  const row = stagedRowOf(staged, id);
  if (!row || row.test) return null;
  const items = {
    source: staged.sources,
    transform: staged.transforms,
    sink: staged.sinks,
  }[row.tab as StagedRole];
  const item = items.find((i) => i.id === id);
  return item ? { role: row.tab as StagedRole, item } : null;
}

function resolveUnstage(
  action: ActionOf<"unstage">,
  state: StudioState,
): Outcome {
  if (state.editsLocked) return refuse(VIEW_ONLY);
  if (state.staged.tests.some((t) => t.id === action.id)) {
    return refuse("tests are removed from the Tests panel");
  }
  const stage = stageOf(state.staged, action.id);
  if (!stage) return refuse(`${displayName(action.id)} is not staged`);
  return accept({
    kind: "unstage",
    id: action.id,
    tab: stage.role,
    untitled: isUntitledScriptId(action.id),
  });
}

function resolveReorder(
  action: ActionOf<"reorder">,
  state: StudioState,
): Outcome {
  if (state.editsLocked) return refuse(VIEW_ONLY);
  const transforms = state.staged.transforms;
  const isTransform = (id: string) => transforms.some((t) => t.id === id);
  if (!isTransform(action.id)) {
    return refuse(
      stageOf(state.staged, action.id)
        ? "only transforms reorder"
        : `${displayName(action.id)} is not a staged transform`,
    );
  }
  const targetId = action.before ?? action.after ?? "";
  if (!isTransform(targetId))
    return refuse(`${displayName(targetId)} is not a staged transform`);
  if (targetId === action.id || transforms.length < 2)
    return refuse("nothing to reorder");
  const edge: DropEdge = action.before ? "top" : "bottom";
  return accept({ kind: "reorder", id: action.id, targetId, edge });
}

function schemaTarget(
  state: StudioState,
  action: ActionOf<"schema.attach" | "schema.detach">,
):
  | { schema: ExplorerItem; stage: { role: StagedRole; item: StagedResource } }
  | Outcome {
  if (state.editsLocked) return refuse(VIEW_ONLY);
  const schema = nodeOf(state.sections, action.schema);
  if (!schema || !action.schema.startsWith("schema:")) {
    return refuse(
      `no such schema in the explorer: ${displayName(action.schema)}`,
    );
  }
  const stage = stageOf(state.staged, action.stage);
  if (!stage) return refuse(`${displayName(action.stage)} is not staged`);
  return { schema, stage };
}

function isAttached(
  schema: StagedSchema,
  role: StagedRole,
  id: string,
): boolean {
  return schema.attachments.some((a) => a.role === role && a.id === id);
}

function resolveAttach(
  action: ActionOf<"schema.attach">,
  state: StudioState,
): Outcome {
  const found = schemaTarget(state, action);
  if ("ok" in found) return found;
  const { schema, stage } = found;
  const current = state.staged.schemas.find((s) =>
    isAttached(s, stage.role, stage.item.id),
  );
  if (current?.id === schema.id) {
    return refuse(`${schema.name} is already attached to ${stage.item.name}`);
  }
  const replaces = stage.role === "sink" && current ? current.name : null;
  return accept({
    kind: "schema.attach",
    resource: toResource(schema),
    target: { role: stage.role, id: stage.item.id },
    tab: stage.role,
    replaces,
  });
}

function resolveDetach(
  action: ActionOf<"schema.detach">,
  state: StudioState,
): Outcome {
  const found = schemaTarget(state, action);
  if ("ok" in found) return found;
  const { schema, stage } = found;
  const staged = state.staged.schemas.find((s) => s.id === schema.id);
  if (!staged || !isAttached(staged, stage.role, stage.item.id)) {
    return refuse(`${schema.name} is not attached to ${stage.item.name}`);
  }
  return accept({
    kind: "schema.detach",
    schemaId: schema.id,
    target: { role: stage.role, id: stage.item.id },
    tab: stage.role,
  });
}

function validJson(text: string): boolean {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}

// An introspected sample of an explorer collection.
function resolveInputFrom(from: string, state: StudioState): Outcome {
  const item = nodeOf(state.sections, from);
  if (!item || !from.startsWith("resource:")) {
    return refuse(`no such item in the explorer: ${displayName(from)}`);
  }
  if (!canPreviewSample(item.provider)) {
    return refuse(`${item.name} can't be introspected`);
  }
  return accept({ kind: "input.load", resource: toResource(item) });
}

function resolveInput(
  action: ActionOf<"input.set">,
  state: StudioState,
): Outcome {
  if (state.editsLocked) return refuse(VIEW_ONLY);
  if (state.view === "full") return refuse(STAGES_FIRST);
  if (action.sample) {
    return accept({ kind: "input.set", source: action.sample });
  }
  if (action.from !== undefined) return resolveInputFrom(action.from, state);
  const text = action.text ?? "";
  if (!validJson(text)) return refuse("not valid JSON");
  return accept({ kind: "input.set", source: "text", text });
}

// A hero template into the transform editor — what the editor's own
// links do (an untitled transform is minted when none is selected).
function resolveScriptTemplate(
  action: Extract<StudioAction, { action: "script.template" }>,
  state: StudioState,
): Outcome {
  if (state.editsLocked) return refuse(VIEW_ONLY);
  if (state.view === "full") return refuse(STAGES_FIRST);
  const template = scriptTemplate(action.template);
  if (!template) return refuse(`no such template: ${action.template}`);
  return accept({
    kind: "script.template",
    script: template.script,
    name: template.label,
    tab: "transform",
  });
}

// The Full pipeline editor's "Insert template" link: only into an empty
// document, as the link itself.
function resolvePipelineTemplate(state: StudioState): Outcome {
  if (state.editsLocked) return refuse(VIEW_ONLY);
  if (state.view !== "full") return refuse("switch to Full pipeline first");
  if (!state.staged.codeEmpty) return refuse("the pipeline code isn't empty");
  return accept({ kind: "pipeline.template" });
}

type ActionOf<K extends StudioAction["action"]> = Extract<
  StudioAction,
  { action: K }
>;
type Resolvers = {
  [K in StudioAction["action"]]: (
    action: ActionOf<K>,
    state: StudioState,
  ) => Outcome;
};

// One resolver per action — the registry of what the Studio can do.
const RESOLVERS: Resolvers = {
  "explorer.expand": resolveExplorer,
  "explorer.collapse": resolveExplorer,
  "explorer.filter": (a) => accept({ kind: "explorer.filter", text: a.text }),
  stage: resolveStage,
  "tab.set": (a, state) =>
    state.view === "full"
      ? refuse(STAGES_FIRST)
      : accept({ kind: "tab.set", tab: a.tab }),
  "view.set": (a, state) => resolveSetView(a.view, state),
  select: resolveSelect,
  "output.set": (a) => accept({ kind: "output.set", collapsed: a.collapsed }),
  "run.pipeline": (_a, state) => resolveRunPipeline(state),
  "run.script": resolveRunScript,
  "run.tests": (_a, state) => resolveRunTests(state),
  unstage: resolveUnstage,
  reorder: resolveReorder,
  "schema.attach": resolveAttach,
  "schema.detach": resolveDetach,
  "input.set": resolveInput,
  "script.template": resolveScriptTemplate,
  "pipeline.template": (_a, state) => resolvePipelineTemplate(state),
};

function outcomeOf(action: StudioAction, state: StudioState): Outcome {
  const resolve = RESOLVERS[action.action] as (
    a: StudioAction,
    s: StudioState,
  ) => Outcome;
  return resolve(action, state);
}

// Ids the label should show by name: the tree's, the pipeline's, or a
// schema instance's schema. actionLabel reads a resource's name off a
// `resource:` id, so a known item is relabelled through one.
function renamedLabel(action: StudioAction, state: StudioState): string {
  const nameOf = (raw: string) => {
    const id = schemaIdOfInstance(raw) ?? raw;
    return (
      nodeOf(state.sections, id)?.name ?? stagedRowOf(state.staged, id)?.name
    );
  };
  const renamed = (id: string) => {
    const name = nameOf(id);
    return name ? `resource:x:${name}` : id;
  };
  if ("ids" in action) {
    return actionLabel({
      ...action,
      ids: action.ids.map((id) => nameOf(id) ?? id),
    });
  }
  const relabelled = { ...action } as Record<string, unknown>;
  for (const field of ["id", "before", "after", "schema", "stage", "from"]) {
    const value = relabelled[field];
    if (typeof value === "string") relabelled[field] = renamed(value);
  }
  return actionLabel(relabelled as StudioAction);
}

// The row's label: a catalog template by its label, everything else by
// the names the Studio knows.
function labelFor(action: StudioAction, state: StudioState): string {
  if (action.action === "script.template") {
    const t = scriptTemplate(action.template);
    return t ? `Script ← ${t.label}` : actionLabel(action);
  }
  return renamedLabel(action, state);
}

export function resolveStudioAction(
  action: StudioAction,
  state: StudioState,
): ResolvedAction {
  const base = {
    group: groupOf(action),
    label: labelFor(action, state),
    button: actionButton(action),
  };
  const outcome = outcomeOf(action, state);
  return outcome.ok
    ? { ...base, ok: true, effect: outcome.effect }
    : { ...base, ok: false, reason: outcome.reason };
}
